/**
 * ScopeSequenceEditorContent
 *
 * Extracted, self-contained scope & sequence editor. Accepts a courseId prop.
 * Used by:
 *   - ScopeSequenceTab (inline in #/curriculum)
 *   - ScopeSequenceEditorModal (modal in #/settings)
 */
import React, { useEffect, useMemo, useState } from "react";
import { scopeSequenceAPI } from "../../services/api";
import "../ScopeSequenceEditorModal.css";

// ---- Helpers (same as original modal) ----

const toDateInput = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  return raw.includes("T") ? raw.slice(0, 10) : raw;
};

const sortEntries = (rows) =>
  [...rows].sort((a, b) => {
    const startA = String(a.start_date || "");
    const startB = String(b.start_date || "");
    if (startA !== startB) return startA.localeCompare(startB);
    return Number(a.unit_number || 0) - Number(b.unit_number || 0);
  });

const formatDate = (value) => {
  const input = toDateInput(value);
  if (!input) return "TBD";
  const parsed = new Date(`${input}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "TBD";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const parseCodes = (value) =>
  String(value || "")
    .split(/[,\n]+/)
    .map((token) => token.trim())
    .filter(Boolean);

const toCodesString = (codes) =>
  Array.isArray(codes) ? codes.join(", ") : "";

const toDraft = (entry) => ({
  school_year: entry.school_year || "",
  nine_weeks_label: entry.nine_weeks_label || "",
  semester_label: entry.semester_label || "",
  unit_number: String(entry.unit_number ?? ""),
  unit_title: entry.unit_title || "",
  ab_day_count:
    entry.ab_day_count === null || typeof entry.ab_day_count === "undefined"
      ? ""
      : String(entry.ab_day_count),
  start_date: toDateInput(entry.start_date),
  end_date: toDateInput(entry.end_date),
  big_idea: entry.big_idea || "",
  teks_readiness_codes: toCodesString(entry.teks_readiness_codes),
  teks_supporting_codes: toCodesString(entry.teks_supporting_codes),
  teks_processing_codes: toCodesString(entry.teks_processing_codes),
  notes: entry.notes || "",
  source_document_title: entry.source_document_title || "",
  source_document_url: entry.source_document_url || "",
});

// ---- Component ----

const ScopeSequenceEditorContent = ({ courseId }) => {
  const [entries, setEntries] = useState([]);
  const [activeSchoolYear, setActiveSchoolYear] = useState("");
  const [selectedEntryId, setSelectedEntryId] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  // Reload when courseId changes
  useEffect(() => {
    if (!courseId) {
      setEntries([]);
      setActiveSchoolYear("");
      setSelectedEntryId("");
      setDraft(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setStatusMessage("");

    const loadEntries = async () => {
      try {
        const response = await scopeSequenceAPI.getAll(courseId);
        if (cancelled) return;
        const rows = sortEntries(
          Array.isArray(response.data) ? response.data : []
        );
        setEntries(rows);
      } catch (loadError) {
        if (cancelled) return;
        setEntries([]);
        setActiveSchoolYear("");
        setSelectedEntryId("");
        setDraft(null);
        setError("Unable to load scope and sequence entries.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEntries();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const schoolYears = useMemo(
    () =>
      [...new Set(entries.map((e) => e.school_year).filter(Boolean))]
        .sort()
        .reverse(),
    [entries]
  );

  const visibleEntries = useMemo(() => {
    const filtered = activeSchoolYear
      ? entries.filter((e) => e.school_year === activeSchoolYear)
      : entries;
    return sortEntries(filtered);
  }, [activeSchoolYear, entries]);

  const selectedEntry = useMemo(
    () => visibleEntries.find((e) => e.id === selectedEntryId) || null,
    [selectedEntryId, visibleEntries]
  );

  // Auto-select school year when entries load or change
  useEffect(() => {
    if (entries.length === 0) {
      setActiveSchoolYear("");
      return;
    }
    const years = [...new Set(entries.map((e) => e.school_year).filter(Boolean))]
      .sort()
      .reverse();
    if (!years.includes(activeSchoolYear)) {
      setActiveSchoolYear(years[0] || "");
    }
  }, [activeSchoolYear, entries]);

  // Auto-select first entry when visible list changes
  useEffect(() => {
    if (visibleEntries.length === 0) {
      setSelectedEntryId("");
      setDraft(null);
      return;
    }
    if (!visibleEntries.some((e) => e.id === selectedEntryId)) {
      setSelectedEntryId(visibleEntries[0].id);
    }
  }, [selectedEntryId, visibleEntries]);

  // Populate draft when selected entry changes
  useEffect(() => {
    if (!selectedEntry) {
      setDraft(null);
      return;
    }
    setDraft(toDraft(selectedEntry));
  }, [selectedEntry]);

  const handleFieldChange = (field, value) => {
    setDraft((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleReset = () => {
    if (!selectedEntry) return;
    setDraft(toDraft(selectedEntry));
  };

  const handleSave = async () => {
    if (!selectedEntry || !draft) return;
    setSaving(true);
    setError("");
    setStatusMessage("");

    try {
      const payload = {
        school_year: draft.school_year.trim(),
        nine_weeks_label: draft.nine_weeks_label.trim(),
        semester_label: draft.semester_label.trim(),
        unit_number: Number(draft.unit_number),
        unit_title: draft.unit_title.trim(),
        ab_day_count:
          draft.ab_day_count === "" ? null : Number(draft.ab_day_count),
        start_date: draft.start_date,
        end_date: draft.end_date,
        big_idea: draft.big_idea.trim(),
        teks_readiness_codes: parseCodes(draft.teks_readiness_codes),
        teks_supporting_codes: parseCodes(draft.teks_supporting_codes),
        teks_processing_codes: parseCodes(draft.teks_processing_codes),
        notes: draft.notes.trim(),
        source_document_title: draft.source_document_title.trim(),
        source_document_url: draft.source_document_url.trim(),
      };

      await scopeSequenceAPI.update(selectedEntry.id, payload);

      const response = await scopeSequenceAPI.getAll(courseId);
      const rows = sortEntries(
        Array.isArray(response.data) ? response.data : []
      );
      setEntries(rows);
      setStatusMessage("Scope and sequence entry saved.");
    } catch (saveError) {
      setError(
        saveError?.response?.data?.error ||
          "Failed to save scope and sequence entry."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Year filter */}
      <div className="scope-editor-controls" style={{ borderTop: "none", paddingTop: 0 }}>
        <label htmlFor="scope-tab-year">
          School Year
          <select
            id="scope-tab-year"
            value={activeSchoolYear}
            onChange={(e) => setActiveSchoolYear(e.target.value)}
            disabled={schoolYears.length === 0}
          >
            {schoolYears.length === 0 && <option value="">N/A</option>}
            {schoolYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* List + form */}
      <div className="scope-editor-body">
        <aside className="scope-editor-list">
          {loading && <p>Loading entries...</p>}
          {!loading && visibleEntries.length === 0 && (
            <p>No scope &amp; sequence entries found for this selection.</p>
          )}
          {!loading &&
            visibleEntries.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`scope-editor-entry-btn ${
                  entry.id === selectedEntryId ? "is-active" : ""
                }`}
                onClick={() => setSelectedEntryId(entry.id)}
              >
                <span>
                  Unit {entry.unit_number}: {entry.unit_title}
                </span>
                <small>
                  {formatDate(entry.start_date)} – {formatDate(entry.end_date)}
                </small>
              </button>
            ))}
        </aside>

        <section className="scope-editor-form">
          {!selectedEntry || !draft ? (
            <p>Select an entry to edit.</p>
          ) : (
            <>
              <div className="scope-editor-grid">
                <label htmlFor="scope-school-year">
                  School Year
                  <input
                    id="scope-school-year"
                    type="text"
                    value={draft.school_year}
                    onChange={(e) =>
                      handleFieldChange("school_year", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-unit-number">
                  Unit Number
                  <input
                    id="scope-unit-number"
                    type="number"
                    min="1"
                    value={draft.unit_number}
                    onChange={(e) =>
                      handleFieldChange("unit_number", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-unit-title" className="scope-editor-full">
                  Unit Title
                  <input
                    id="scope-unit-title"
                    type="text"
                    value={draft.unit_title}
                    onChange={(e) =>
                      handleFieldChange("unit_title", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-nine-weeks">
                  Nine Weeks
                  <input
                    id="scope-nine-weeks"
                    type="text"
                    value={draft.nine_weeks_label}
                    onChange={(e) =>
                      handleFieldChange("nine_weeks_label", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-semester">
                  Semester
                  <input
                    id="scope-semester"
                    type="text"
                    value={draft.semester_label}
                    onChange={(e) =>
                      handleFieldChange("semester_label", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-ab-days">
                  A/B Day Count
                  <input
                    id="scope-ab-days"
                    type="number"
                    min="0"
                    value={draft.ab_day_count}
                    onChange={(e) =>
                      handleFieldChange("ab_day_count", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-start-date">
                  Start Date
                  <input
                    id="scope-start-date"
                    type="date"
                    value={draft.start_date}
                    onChange={(e) =>
                      handleFieldChange("start_date", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-end-date">
                  End Date
                  <input
                    id="scope-end-date"
                    type="date"
                    value={draft.end_date}
                    onChange={(e) =>
                      handleFieldChange("end_date", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-big-idea" className="scope-editor-full">
                  Big Idea
                  <textarea
                    id="scope-big-idea"
                    rows="3"
                    value={draft.big_idea}
                    onChange={(e) =>
                      handleFieldChange("big_idea", e.target.value)
                    }
                  />
                </label>

                <label
                  htmlFor="scope-readiness"
                  className="scope-editor-full"
                >
                  TEKS Readiness (comma separated)
                  <textarea
                    id="scope-readiness"
                    rows="2"
                    value={draft.teks_readiness_codes}
                    onChange={(e) =>
                      handleFieldChange("teks_readiness_codes", e.target.value)
                    }
                  />
                </label>

                <label
                  htmlFor="scope-supporting"
                  className="scope-editor-full"
                >
                  TEKS Supporting (comma separated)
                  <textarea
                    id="scope-supporting"
                    rows="2"
                    value={draft.teks_supporting_codes}
                    onChange={(e) =>
                      handleFieldChange(
                        "teks_supporting_codes",
                        e.target.value
                      )
                    }
                  />
                </label>

                <label
                  htmlFor="scope-processing"
                  className="scope-editor-full"
                >
                  TEKS Processing (comma separated)
                  <textarea
                    id="scope-processing"
                    rows="2"
                    value={draft.teks_processing_codes}
                    onChange={(e) =>
                      handleFieldChange(
                        "teks_processing_codes",
                        e.target.value
                      )
                    }
                  />
                </label>

                <label htmlFor="scope-notes" className="scope-editor-full">
                  Notes
                  <textarea
                    id="scope-notes"
                    rows="2"
                    value={draft.notes}
                    onChange={(e) =>
                      handleFieldChange("notes", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-source-title">
                  Source Document Title
                  <input
                    id="scope-source-title"
                    type="text"
                    value={draft.source_document_title}
                    onChange={(e) =>
                      handleFieldChange("source_document_title", e.target.value)
                    }
                  />
                </label>

                <label htmlFor="scope-source-url">
                  Source Document URL
                  <input
                    id="scope-source-url"
                    type="text"
                    value={draft.source_document_url}
                    onChange={(e) =>
                      handleFieldChange("source_document_url", e.target.value)
                    }
                  />
                </label>
              </div>

              <div className="scope-editor-actions">
                <button
                  type="button"
                  className="primary-btn"
                  title="Revert unsaved edits"
                  disabled={saving || !selectedEntry || !draft}
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={saving || !selectedEntry || !draft}
                  onClick={handleSave}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      {(error || statusMessage) && (
        <div className="scope-editor-feedback">
          {error && <p className="scope-editor-error">{error}</p>}
          {statusMessage && (
            <p className="scope-editor-success">{statusMessage}</p>
          )}
        </div>
      )}
    </>
  );
};

export default ScopeSequenceEditorContent;
